import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Select } from 'components/Form/Select/Select';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { Option } from 'types/Generics';

import classes from './DatePickerSpinner.module.scss';

const DatePickerMode = {
    Month: 'month',
    Week: 'week'
} as const;

type DatePickerBaseProps = {
    timeZone: string;
    selectedDate: Moment;
    loading?: boolean;
    maxDate?: Moment;
    minDate?: Moment;
    onChange?: (date: Moment) => void;
};

type MonthPickerModeProps = DatePickerBaseProps & {
    mode: typeof DatePickerMode.Month;
};

type WeekPickerModeProps = DatePickerBaseProps & {
    mode: typeof DatePickerMode.Week;
};

type DatePickerSpinnerProps = MonthPickerModeProps | WeekPickerModeProps;

export const DatePickerSpinner: React.FC<DatePickerSpinnerProps> = ({
    mode,
    timeZone,
    selectedDate,
    loading,
    minDate,
    maxDate,
    onChange
}) => {
    const today = moment().tz(timeZone);

    const [startOfWeek, setStartOfWeek] = React.useState<Moment>(
        selectedDate.clone().startOf('isoWeek')
    );

    const isCurrentWeek = React.useMemo(() => {
        if (mode === DatePickerMode.Week) {
            return startOfWeek.isSame(today, 'isoWeek');
        }
        return false;
    }, [mode, today, startOfWeek]);

    const monthOptions = React.useMemo(() => {
        const options = [];
        const currentDate = today.clone().startOf('month');

        for (let i = 0; i <= 12; i++) {
            options.push({
                id: currentDate.clone().add(i, 'months').format('YYYY-MM'),
                name: currentDate.clone().add(i, 'months').format('MMMM YYYY')
            });
        }

        return options;
    }, [today]);

    const dateRange = React.useMemo(() => {
        const range: Array<Moment> = [];
        let startDate: Moment;
        let endDate: Moment;

        if (mode === DatePickerMode.Week) {
            startDate = startOfWeek.clone();
            endDate = startDate.clone().endOf('isoWeek');
        } else {
            startDate = selectedDate.clone().startOf('month');

            if (minDate && startDate.isBefore(minDate, 'day')) {
                startDate = minDate.clone();
            }

            if (startDate.isBefore(today, 'day')) {
                startDate = today.clone();
            }

            endDate = startDate.clone().endOf('month');
        }

        const currentDate = startDate.clone();
        while (currentDate.isSameOrBefore(endDate)) {
            range.push(currentDate.clone());
            currentDate.add(1, 'day');
        }

        return range;
    }, [mode, selectedDate, minDate, startOfWeek, today]);

    const isPreviousWeekDisabled = React.useMemo(() => {
        if (mode === DatePickerMode.Week) {
            return startOfWeek.isSameOrBefore(minDate, 'isoWeek');
        }
        return false;
    }, [mode, minDate, startOfWeek]);

    const isNextWeekDisabled = React.useMemo(() => {
        if (mode === DatePickerMode.Week) {
            return startOfWeek.isSameOrAfter(maxDate, 'isoWeek');
        }
        return false;
    }, [mode, maxDate, startOfWeek]);

    const dayClickHandler = (day: Moment) => {
        if (minDate && day.isBefore(minDate, 'day')) {
            return;
        }

        if (maxDate && day.isAfter(maxDate, 'day')) {
            return;
        }

        onChange?.(day.clone());
    };

    const nextWeekClickHandler = () => {
        if (mode === DatePickerMode.Week) {
            setStartOfWeek(startOfWeek.clone().add(1, 'week'));
        }
    };

    const previousWeekClickHandler = () => {
        if (mode === DatePickerMode.Week) {
            setStartOfWeek(startOfWeek.clone().subtract(1, 'week'));
        }
    };

    const monthChangeHandler = (value: Option<string>) => {
        const selectedMonth = moment(value.id, 'YYYY-MM').tz(timeZone, true);

        if (minDate && selectedMonth.isBefore(minDate, 'day')) {
            return onChange?.(minDate.clone());
        }

        onChange?.(selectedMonth);
    };

    React.useEffect(() => {
        if (mode === DatePickerMode.Week) {
            setStartOfWeek(selectedDate.clone().startOf('isoWeek'));
        }
    }, [mode, selectedDate]);

    return (
        <div
            className={clsx(classes.root, {
                [classes.loading]: loading,
                [classes.monthPickerMode]: mode === DatePickerMode.Month
            })}
        >
            {mode === DatePickerMode.Month && (
                <>
                    <Select
                        options={monthOptions}
                        onChange={monthChangeHandler}
                        className={classes.monthSelect}
                        value={selectedDate.format('YYYY-MM')}
                    />
                </>
            )}

            <div className={classes.header}>
                <p className={classes.dateLabel}>
                    {mode === DatePickerMode.Week
                        ? startOfWeek.format('MMMM YYYY')
                        : selectedDate.format('MMMM YYYY')}
                </p>

                {isCurrentWeek && <span className={classes.currentWeekTag}>THIS WEEK</span>}
            </div>

            <div className={classes.spinner}>
                <button
                    type="button"
                    className={clsx(classes.controlButton, {
                        [classes.controlButtonDisabled]: isPreviousWeekDisabled
                    })}
                    onClick={previousWeekClickHandler}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                {dateRange.map((day, i) => (
                    <button
                        key={i}
                        type="button"
                        className={clsx(classes.dayButton, {
                            [classes.dayButtonSelected]: day.isSame(selectedDate, 'day'),
                            [classes.dayButtonDisabled]:
                                day.isBefore(minDate, 'day') || day.isAfter(maxDate, 'day')
                        })}
                        onClick={() => dayClickHandler(day)}
                    >
                        <span className={classes.dayWeekday}>{day.format('ddd')}</span>

                        <span className={classes.dayNumber}>{day.format('DD')}</span>
                    </button>
                ))}

                <button
                    type="button"
                    className={clsx(classes.controlButton, {
                        [classes.controlButtonDisabled]: isNextWeekDisabled
                    })}
                    onClick={nextWeekClickHandler}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
        </div>
    );
};
